'use client';

// @boty/ui - Card component
// Wraps MUI Card with Boty styling (12px border radius)

import type { ReactNode } from 'react';
import MuiCard, { type CardProps as MuiCardProps } from '@mui/material/Card';
import CardContent, { type CardContentProps } from '@mui/material/CardContent';
import CardActions, { type CardActionsProps } from '@mui/material/CardActions';
import CardHeader, { type CardHeaderProps } from '@mui/material/CardHeader';

export interface CardProps extends Omit<MuiCardProps, 'children'> {
  children: ReactNode;
}

/**
 * Card - Styled MUI Card with Boty theme (12px border radius)
 *
 * @param children - Card content
 * @param elevation - Shadow depth (0-24), default uses theme shadow
 * @param variant - 'elevation' (default) or 'outlined'
 * @param ...props - All standard MUI Card props
 *
 * @example
 * ```tsx
 * <Card>
 *   <Card.Content>
 *     <Typography variant="h6">Title</Typography>
 *     <Typography>Card content here</Typography>
 *   </Card.Content>
 *   <Card.Actions>
 *     <Button>Action</Button>
 *   </Card.Actions>
 * </Card>
 * ```
 */
function CardRoot({ children, ...props }: CardProps): ReactNode {
  return <MuiCard {...props}>{children}</MuiCard>;
}
CardRoot.displayName = 'Card';

/**
 * Card.Content - Card content wrapper with padding
 */
function CardContentWrapper({
  children,
  ...props
}: CardContentProps): ReactNode {
  return <CardContent {...props}>{children}</CardContent>;
}
CardContentWrapper.displayName = 'Card.Content';

/**
 * Card.Actions - Card actions container (typically for buttons)
 */
function CardActionsWrapper({
  children,
  ...props
}: CardActionsProps): ReactNode {
  return <CardActions {...props}>{children}</CardActions>;
}
CardActionsWrapper.displayName = 'Card.Actions';

/**
 * Card.Header - Card header with title, subheader, and optional avatar/action
 */
function CardHeaderWrapper(props: CardHeaderProps): ReactNode {
  return <CardHeader {...props} />;
}
CardHeaderWrapper.displayName = 'Card.Header';

// Attach subcomponents to Card
export const Card = Object.assign(CardRoot, {
  Content: CardContentWrapper,
  Actions: CardActionsWrapper,
  Header: CardHeaderWrapper,
});

// Export individual components for tree-shaking
export { CardContentWrapper as CardContent };
export { CardActionsWrapper as CardActions };
export { CardHeaderWrapper as CardHeader };

// Re-export MUI types for consumers
export type { CardContentProps, CardActionsProps, CardHeaderProps };
